package com.sddamico.draftmachine.components.sections

import androidx.compose.runtime.Composable
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.core.Avatar
import com.sddamico.draftmachine.ds.core.AvatarSize
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.displayInlineFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.brand.Logo
import com.sddamico.draftmachine.ds.brand.LogoVariant
import com.sddamico.draftmachine.ds.motion.TransitionRole
import com.sddamico.draftmachine.ds.motion.transitionRole
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

val AppHeaderStyle = CssStyle {
  base { Modifier.height(56.px).fillMaxWidth() }
  Breakpoint.MD {
    Modifier.height(62.px).styleModifier {
      property("width", "100vw")
      property("margin-left", "calc(50% - 50vw)")
      property("margin-right", "calc(50% - 50vw)")
    }
  }
}

val AppHeaderContentStyle = CssStyle {
  base {
    Modifier.gap(10.px).padding(leftRight = 16.px)
  }
  Breakpoint.MD {
    Modifier.gap(14.px).padding(leftRight = 24.px)
  }
}

val AppHeaderMobileStyle = CssStyle {
  base { Modifier.displayFlex() }
  Breakpoint.MD { Modifier.styleModifier { property("display", "none") } }
}

val AppHeaderDesktopStyle = CssStyle {
  base { Modifier.styleModifier { property("display", "none") } }
  Breakpoint.MD { Modifier.displayFlex() }
}

/** Top-level app sections reachable from the header nav. */
enum class AppNav(val label: String, val route: String) {
  Drafts("Drafts", "/"),
  Pools("Pools", "/pools"),
}

/**
 * The authenticated app chrome — the full wordmark on the machine room, or a back affordance and
 * stamped [title] on detail screens, plus an email-derived avatar that links to account settings.
 * Reused across every logged-in screen; parameterize [title] per screen. Pass [nav] on top-level
 * screens to show the Drafts/Pools section links (with the active one underlined in brass).
 */
@Composable
fun AppHeader(
  email: String,
  modifier: Modifier = Modifier,
  title: String = "Machine room",
  onBack: (() -> Unit)? = null,
  onAccount: (() -> Unit)? = null,
  nav: AppNav? = null,
) {
  val ctx = rememberPageContext()
  val openAccount = onAccount ?: { ctx.router.transitionTo("/settings") }

  Div(
    attrs = Modifier
      .then(AppHeaderStyle.toModifier())
      .transitionRole(TransitionRole.Header)
      .displayFlex().alignItems(AlignItems.Center)
      .borderBottom(1.px, LineStyle.Solid, DmColor.border)
      .backgroundColor(Color.rgba(22, 28, 34, 0.85f))
      .then(modifier)
      .toAttrs()
  ) {
    Div(
      attrs = AppHeaderContentStyle.toModifier()
        .displayFlex().alignItems(AlignItems.Center)
        .fillMaxWidth()
        .toAttrs()
    ) {
      Div(attrs = AppHeaderMobileStyle.toModifier().alignItems(AlignItems.Center).gap(10.px).toAttrs()) {
        if (onBack != null) {
          IconButton(
            icon = "caret-left", label = "Back",
            variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
            onClick = onBack,
          )
          SpanText(title, Modifier.dmStamp())
        } else if (nav != null) {
          Logo(variant = LogoVariant.Full, size = 22.0)
          NavLinks(nav) { ctx.router.transitionTo(it.route) }
        } else {
          Logo(variant = LogoVariant.Full, size = 22.0)
        }
      }
      Div(attrs = AppHeaderDesktopStyle.toModifier().alignItems(AlignItems.Center).gap(14.px).toAttrs()) {
        if (onBack != null) {
          IconButton(
            icon = "caret-left", label = "Back",
            variant = ButtonVariant.Ghost, size = ButtonSize.Sm,
            onClick = onBack,
          )
        }
        Logo(variant = LogoVariant.Full, size = 22.0)
        if (nav != null) NavLinks(nav, desktop = true) { ctx.router.transitionTo(it.route) }
      }
      Div(attrs = Modifier.flex(1).toAttrs()) {}
      SpanText(title, AppHeaderDesktopStyle.toModifier().alignItems(AlignItems.Center).dmStamp().color(DmColor.textMuted))
      Span(
        attrs = Modifier.displayInlineFlex()
          .cursor(Cursor.Pointer)
          .toAttrs {
            attr("role", "button")
            attr("aria-label", "Account settings")
            attr("title", "Account settings")
            onClick { openAccount() }
          }
      ) {
        Avatar(name = displayNameFromEmail(email), size = AvatarSize.Sm)
      }
    }
  }
}

/** The Drafts/Pools section stamps — active one brass with an underline, per frames 1a–1b. */
@Composable
private fun NavLinks(active: AppNav, desktop: Boolean = false, onNavigate: (AppNav) -> Unit) {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(14.px).margin(left = 6.px).toAttrs()) {
    AppNav.entries.forEach { item ->
      val isActive = item == active
      Span(
        attrs = Modifier
          .dmStamp()
          .then(if (desktop) Modifier.fontSize(DmType.textSm) else Modifier)
          .position(Position.Relative)
          .padding(bottom = 5.px)
          .color(if (isActive) DmColor.brass400 else DmColor.textMuted)
          .then(if (isActive) Modifier else Modifier.cursor(Cursor.Pointer))
          .toAttrs {
            attr("role", "link")
            attr("aria-current", if (isActive) "page" else "false")
            if (!isActive) onClick { onNavigate(item) }
          }
      ) {
        org.jetbrains.compose.web.dom.Text(item.label)
        if (isActive) {
          Span(
            attrs = Modifier.position(Position.Absolute)
              .left(0.px).right(0.px).bottom(0.px).height(2.px)
              .backgroundColor(DmColor.brass500)
              .transitionRole(TransitionRole.NavUnderline)
              .toAttrs()
          )
        }
      }
    }
  }
}

/** Turn an email local-part into a display name so the avatar shows sensible initials. */
private fun displayNameFromEmail(email: String): String {
  val local = email.substringBefore("@")
  return local.split(".", "_", "+", "-")
    .filter { it.isNotBlank() }
    .joinToString(" ") { it.replaceFirstChar { c -> c.uppercase() } }
    .ifBlank { email }
}
